/**
 * Zonamento logo (BRAND.md "Logo & wordmark"; plan mid-v6k8, decision 6).
 *
 * BrandLogo: primary lockup, three stacked squares (purple, teal, white) left
 * of the wordmark "miD".
 * BrandMark: the 2x2 mark, the fourth square is paper with diagonal hatching.
 *
 * - The wordmark is Roboto Condensed Bold at 44 units, outlined once with
 *   fontTools (no font needed at runtime). Rerun the outline only if the
 *   lockup geometry changes; nothing in the repo downloads the font.
 * - Ink edges and hatching are 1 CSS px at every size (non-scaling stroke)
 *   and the squares snap to whole pixels (crispEdges), so the white square keeps
 *   its edge on white at 16px and at 200px.
 * - tone "auto" reads --brand-* and so follows the scope: day by default,
 *   night inside .cockpit-root / .agent-cockpit. tone "night" forces the night
 *   ink and paper for dark surfaces outside a night scope.
 */

// mid-yb9j: the logo keeps the original colours. Its own names, not the zoning tokens:
// those also colour map land use and Match tiers.
const LOGO_SQUARE = {
  purple: 'var(--brand-logo-purple, #A78BD9)',
  teal: 'var(--brand-logo-teal, #3CC8B5)',
  white: 'var(--brand-logo-white, #FFFFFF)',
};

// ponytail: the night tokens only exist inside the night scopes, so a forced night
// tone carries DESIGN.md's night ink and surface as fallbacks of a scope-free name.
const TONES = {
  auto: { ink: 'var(--brand-ink, #1A1A1A)', paper: 'var(--brand-surface, #FFFFFF)' },
  night: { ink: 'var(--brand-logo-night-ink, #EEF0F2)', paper: 'var(--brand-logo-night-paper, #1B1E24)' },
};

// "miD", Roboto Condensed Bold 700, 44 units, baseline y=45, origin x=27.
const WORDMARK = 'M35.29 26.57V45H29.26V21.75H34.91ZM34.46 32.58 32.78 32.62Q32.74 30.18 33.21 28.11Q33.68 26.05 34.64 24.53Q35.59 23 37.07 22.16Q38.54 21.32 40.47 21.32Q41.72 21.32 42.8 21.79Q43.89 22.25 44.67 23.23Q45.46 24.2 45.88 25.76Q46.31 27.32 46.31 29.49V45H40.32V30.22Q40.32 28.67 40.02 27.84Q39.72 27.02 39.16 26.68Q38.6 26.35 37.79 26.35Q36.93 26.35 36.28 26.82Q35.64 27.3 35.24 28.15Q34.84 28.99 34.64 30.13Q34.43 31.27 34.46 32.58ZM45.76 31.98 43.61 32.26Q43.54 29.96 43.99 27.96Q44.45 25.96 45.39 24.48Q46.34 23 47.76 22.16Q49.19 21.32 51.08 21.32Q52.5 21.32 53.66 21.8Q54.82 22.27 55.65 23.31Q56.48 24.35 56.92 26.04Q57.36 27.73 57.36 30.18V45H51.32V30.2Q51.32 28.63 51.03 27.79Q50.74 26.95 50.18 26.65Q49.62 26.35 48.83 26.35Q48.05 26.35 47.49 26.78Q46.92 27.21 46.53 27.98Q46.14 28.76 45.95 29.77Q45.76 30.78 45.76 31.98ZM68.19 21.75V45H62.15V21.75ZM61.87 15.72Q61.87 14.32 62.76 13.42Q63.65 12.52 65.18 12.52Q66.68 12.52 67.56 13.42Q68.44 14.32 68.44 15.72Q68.44 17.11 67.56 18.03Q66.68 18.94 65.18 18.94Q63.65 18.94 62.76 18.03Q61.87 17.11 61.87 15.72ZM81.42 45H75.9L75.94 39.8H81.42Q83.7 39.8 84.96 38.98Q86.23 38.17 86.76 36.41Q87.29 34.64 87.29 31.79V26.91Q87.29 24.74 87.01 23.23Q86.73 21.71 86.06 20.78Q85.39 19.84 84.28 19.39Q83.16 18.94 81.51 18.94H75.79V13.72H81.51Q84.21 13.72 86.43 14.6Q88.64 15.48 90.23 17.17Q91.82 18.85 92.68 21.32Q93.54 23.79 93.54 26.95V31.79Q93.54 34.97 92.68 37.42Q91.82 39.87 90.24 41.55Q88.66 43.24 86.43 44.12Q84.19 45 81.42 45ZM79.55 13.72V45H73.26V13.72Z';

const LOCKUP_W = 95;
const LOCKUP_H = 55;

// vector-effect does not inherit, so every edged shape carries it.
const NS = { vectorEffect: 'non-scaling-stroke' };

const a11y = (label) => (label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true });

/** Primary lockup. height in px; width follows the 95:55 box. */
export function BrandLogo({ height = 40, tone = 'auto', label = 'miD', className, style, ...rest }) {
  const { ink } = TONES[tone] || TONES.auto;
  return (
    <svg
      viewBox={`0 0 ${LOCKUP_W} ${LOCKUP_H}`}
      width={(height * LOCKUP_W) / LOCKUP_H}
      height={height}
      className={className}
      style={{ display: 'block', flexShrink: 0, ...style }}
      data-brand-art="lockup"
      {...a11y(label)}
      {...rest}
    >
      <g stroke={ink} strokeWidth="1" shapeRendering="crispEdges">
        <rect x="1" y="1" width="15" height="15" fill={LOGO_SQUARE.purple} {...NS} />
        <rect x="1" y="20" width="15" height="15" fill={LOGO_SQUARE.teal} {...NS} />
        <rect x="1" y="39" width="15" height="15" fill={LOGO_SQUARE.white} {...NS} />
      </g>
      <path d={WORDMARK} fill={ink} />
    </svg>
  );
}

/** 2x2 mark (BRAND.md reference geometry). size in px. */
export function BrandMark({ size = 32, tone = 'auto', label = 'miD', className, style, ...rest }) {
  const { ink, paper } = TONES[tone] || TONES.auto;
  return (
    <svg
      viewBox="0 0 32 32"
      width={size}
      height={size}
      className={className}
      style={{ display: 'block', flexShrink: 0, ...style }}
      data-brand-art="mark"
      {...a11y(label)}
      {...rest}
    >
      <g stroke={ink} strokeWidth="1" shapeRendering="crispEdges">
        <rect x="2" y="2" width="13" height="13" fill={LOGO_SQUARE.purple} {...NS} />
        <rect x="17" y="2" width="13" height="13" fill={LOGO_SQUARE.teal} {...NS} />
        <rect x="2" y="17" width="13" height="13" fill={LOGO_SQUARE.white} {...NS} />
        <rect x="17" y="17" width="13" height="13" fill={paper} {...NS} />
      </g>
      <path d="M17 26L26 17M17 30L30 17M21 30L30 21" fill="none" stroke={ink} strokeWidth="1" {...NS} />
    </svg>
  );
}

export default BrandLogo;
